import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { useQuery } from "convex/react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { TemplateFormEnum, TypeEvalEnum } from "../../../../convex/schema";
import { PamiResultsEcharts } from "./results/PamiResultsEcharts";
import {
  AutodiagResults,
  type ReferentielAutodiag,
} from "./results/AutodiagResults";
import referentielEnedis from "../../../../referentiel_enedis.json";
import { EgogrammeResults } from "./results/EgogrammeResults";
import { EntStructResults } from "./results/EntStructResults";
import { InbasketResults } from "./results/InbasketResults";
import { Copsoq3Results } from "./results/Copsoq3Results";

export function EvaluationResults() {
  const { parcoursId, evaluationId } = useParams<{
    parcoursId: string;
    evaluationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const fromPath = searchParams.get("from");
  const backLink = fromPath || `/p/${parcoursId}`;
  const backLabel = fromPath ? "Retour à la campagne" : "Retour au parcours";

  const result = useQuery(
    api.evaluations.obtenirEvaluationPourAdmin,
    evaluationId ? { evaluationId: evaluationId as Id<"evaluations"> } : "skip",
  );

  // Formatage de la date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Formatage du temps passé
  const formatTempsPasse = (secondes: number) => {
    const heures = Math.floor(secondes / 3600);
    const minutes = Math.floor((secondes % 3600) / 60);
    const secondesRestantes = secondes % 60;

    if (heures > 0) {
      return `${heures}h ${minutes}min ${secondesRestantes}s`;
    }
    if (minutes > 0) {
      return `${minutes}min ${secondesRestantes}s`;
    }
    return `${secondesRestantes}s`;
  };

  // Obtenir le titre de l'évaluation
  const getEvaluationTitle = () => {
    if (!result) return "";
    const { evaluation } = result;

    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      const donnees = evaluation.donnees as {
        type?: string;
        template?: string;
      };
      if (donnees.type === "form") {
        switch (donnees.template) {
          case TemplateFormEnum.AUTODIAG:
            return "Autodiagnostic";
          case TemplateFormEnum.EGOGRAMME:
            return "Egogramme";
          case TemplateFormEnum.PAMI:
            return "Test de personnalité";
          default:
            return "Questionnaire";
        }
      }
    }
    if (evaluation.typeEval === TypeEvalEnum.INBASKET) {
      return "Évaluation In-Basket";
    }
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return "Entretien Structuré";
    }
    return "Évaluation";
  };

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <div className="w-full min-w-0 p-4 md:p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Évaluation introuvable
          </h1>
          <p className="mt-2 text-muted-foreground">
            L'évaluation demandée n'existe pas.
          </p>
          <Link to={backLink} className="mt-4 inline-block">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { evaluation, tempsPasse } = result;

  const candidatInfo = {
    prenom: evaluation.prenomCandidat,
    nom: evaluation.nomCandidat,
    email: evaluation.emailCandidat,
    dateDebut: evaluation.dateDebut,
    dateFin: evaluation.dateFin,
    tempsPasse,
  };

  const renderResults = () => {
    if (evaluation.typeEval === TypeEvalEnum.INBASKET) {
      return (
        <InbasketResults
          donnees={evaluation.donnees}
          candidatInfo={candidatInfo}
        />
      );
    }

    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      const donnees = evaluation.donnees as {
        type?: string;
        template?: string;
      };
      if (donnees.type === "form") {
        if (donnees.template === TemplateFormEnum.AUTODIAG) {
          return (
            <AutodiagResults
              donnees={evaluation.donnees}
              referentiel={referentielEnedis as ReferentielAutodiag}
              candidatInfo={candidatInfo}
            />
          );
        }
        if (donnees.template === TemplateFormEnum.EGOGRAMME) {
          return (
            <EgogrammeResults
              donnees={evaluation.donnees}
              candidatInfo={candidatInfo}
            />
          );
        }
        if (donnees.template === TemplateFormEnum.COPSOQ3) {
          return (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <Copsoq3Results
              donnees={evaluation.donnees as any}
              candidatInfo={candidatInfo}
            />
          );
        }
        if (donnees.template === TemplateFormEnum.PAMI) {
          return <PamiResultsEcharts donnees={evaluation.donnees} candidatInfo={candidatInfo} />;
        }
      }
      return <div>Type de formulaire inconnu</div>;
    }

    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return (
        <EntStructResults
          donnees={evaluation.donnees}
          candidatInfo={candidatInfo}
        />
      );
    }

    return <div>Type d'évaluation non supporté</div>;
  };

  return (
    <div className="w-full min-w-0 p-4 md:p-8" style={{ scrollbarGutter: "stable" }}>
      {/* Header */}
      <div className="mb-6">
        <Link
          to={backLink}
          className="text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4 inline mr-1" />
          {backLabel}
        </Link>
      </div>

      {renderResults()}

      {/* Retours d'expérience */}
      {evaluation.retoursExperience && (
        <Card>
          <CardHeader>
            <CardTitle>Retours d'expérience</CardTitle>
            <CardDescription>
              Commentaires du candidat sur l'évaluation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(evaluation.retoursExperience).map(
                ([key, value]) => (
                  <div key={key} className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {key}
                    </p>
                    <p className="whitespace-pre-wrap">{value}</p>
                  </div>
                ),
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
